import { Monitor, Smartphone } from 'lucide-react';
import { appShellColors } from '../styles/appShell';
import { getAccessibleTextColor } from '../utils/color';
import type { Locale } from '../i18n';

export type Viewport = 'desktop' | 'mobile';

interface ViewportConfig {
  width: string | number;
}

export const VIEWPORTS: Record<Viewport, ViewportConfig> = {
  desktop: { width: '100%' },
  mobile: { width: 375 },
};

const viewportLabels: Record<Locale, Record<Viewport, string>> = {
  zh: {
    desktop: '桌面端',
    mobile: '移动端',
  },
  en: {
    desktop: 'Desktop',
    mobile: 'Mobile',
  },
};

const viewportIcons: Record<Viewport, (props: { className?: string; size?: number }) => JSX.Element> = {
  desktop: (props) => <Monitor {...props} />,
  mobile: (props) => <Smartphone {...props} />,
};

interface ViewportSwitcherProps {
  current: Viewport;
  onChange: (viewport: Viewport) => void;
  locale?: Locale;
  theme?: {
    primary: string;
    background: string;
    text: string;
    surface: string;
    border: string;
  };
}

export function ViewportSwitcher({ current, onChange, locale = 'en', theme }: ViewportSwitcherProps) {
  const defaultTheme = {
    primary: appShellColors.primary,
    background: appShellColors.background,
    text: appShellColors.textPrimary,
    surface: appShellColors.surface,
    border: appShellColors.border,
  };
  const t = theme || defaultTheme;

  return (
    <div
      className="grid min-h-11 w-[132px] flex-shrink-0 grid-cols-2 items-center gap-1 rounded-lg p-1 sm:w-[208px]"
      style={{
        backgroundColor: t.surface,
        border: `1px solid ${t.border}`,
      }}
    >
      {(Object.keys(VIEWPORTS) as Viewport[]).map((viewport) => {
        const Icon = viewportIcons[viewport];
        const isActive = current === viewport;
        const label = viewportLabels[locale][viewport];

        return (
          <button
            key={viewport}
            type="button"
            onClick={() => onChange(viewport)}
            className="flex min-h-11 min-w-0 items-center justify-center gap-1.5 rounded-md px-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 sm:px-3"
            style={{
              backgroundColor: isActive ? t.primary : 'transparent',
              color: isActive ? getAccessibleTextColor(appShellColors.onPrimary, t.primary) : t.text,
              ['--tw-ring-color' as string]: t.primary,
            }}
            aria-pressed={isActive}
            aria-label={label}
            title={label}
          >
            <Icon size={16} aria-hidden="true" className="flex-shrink-0" />
            <span className="hidden min-w-0 truncate sm:inline">{label}</span>
          </button>
        );
      })}
    </div>
  );
}
